import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Pie } from "react-chartjs-2";
import { API_URL } from "../../../config";
import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import "./ReportsDashboard.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

function ReportsDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!showDropdown) return;
    const handleOutsideClick = () => setShowDropdown(false);
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, [showDropdown]);

  useEffect(() => {
    fetch(`${API_URL}/api/reports/summary`)
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch data");
        return response.json();
      })
      .then((json) => {
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const totals = useMemo(() => {
    if (!data) {
      return {
        totalProposals: 0,
        approvedProposals: 0,
        pendingProposals: 0,
        totalInvoices: 0,
        paidInvoices: 0,
        pendingInvoices: 0,
        partialPendingAmount: 0,
      };
    }

    return {
      totalProposals: data.proposals?.total || 0,
      approvedProposals: data.proposals?.approved || 0,
      pendingProposals: data.proposals?.pending || 0,
      totalInvoices: data.invoices?.total || 0,
      paidInvoices: data.invoices?.paid || 0,
      pendingInvoices: data.invoices?.pending || 0,
      partialPendingAmount: data.invoices?.partialPendingAmount ?? 0,
    };
  }, [data]);

  const downloadPDFReport = (type = "full") => {
    if (!data) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    const truncateText = (text, maxLength) => {
      if (!text) return "";
      text = String(text);
      return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    };

    // Header Band
    doc.setFillColor(11, 31, 58);
    doc.rect(0, 0, pageWidth, 40, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont("helvetica", "bold");
    doc.text("PREMIER WEBTECH", 14, 18);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    const subTitle = type === "full"
      ? "Business Intelligence & Performance Report"
      : type === "proposals"
        ? "Proposals Intelligence Report"
        : "Invoices & Billing Performance Report";
    doc.text(subTitle, 14, 25);
    doc.text(`Generated on: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, 14, 32);

    y = 55;

    if (type === "full" || type === "proposals") {
      // Proposals Summary Section
      doc.setTextColor(11, 31, 58);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Proposals Summary", 14, y);
      doc.setDrawColor(200, 200, 200);
      doc.line(14, y + 2, pageWidth - 14, y + 2);

      y += 12;
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Metric Description", 14, y);
      doc.text("Value / Count", 120, y);
      doc.line(14, y + 2, pageWidth - 14, y + 2);

      y += 10;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);
      doc.text("Total Proposals Submitted", 14, y);
      doc.text(String(totals.totalProposals), 120, y);
      y += 8;
      doc.text("Approved Proposals (Accepted)", 14, y);
      doc.text(String(totals.approvedProposals), 120, y);
      y += 8;
      doc.text("Pending Proposals", 14, y);
      doc.text(String(totals.pendingProposals), 120, y);

      const conversionRate = totals.totalProposals ? ((totals.approvedProposals / totals.totalProposals) * 100).toFixed(1) : 0;
      y += 8;
      doc.text("Proposal Conversion Rate", 14, y);
      doc.text(`${conversionRate}%`, 120, y);

      y += 18;
    }

    if (type === "full" || type === "invoices") {
      // Invoices Summary Section
      doc.setTextColor(11, 31, 58);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text("Invoices & Billing Summary", 14, y);
      doc.setDrawColor(200, 200, 200);
      doc.line(14, y + 2, pageWidth - 14, y + 2);

      y += 12;
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("Metric Description", 14, y);
      doc.text("Value", 120, y);
      doc.line(14, y + 2, pageWidth - 14, y + 2);

      y += 10;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);
      doc.text("Total Invoices Generated", 14, y);
      doc.text(String(totals.totalInvoices), 120, y);
      y += 8;
      doc.text("Paid Invoices", 14, y);
      doc.text(String(totals.paidInvoices), 120, y);
      y += 8;
      doc.text("Pending Invoices (Unpaid/Partial)", 14, y);
      doc.text(String(totals.pendingInvoices), 120, y);
      y += 8;
      doc.text("Partial Payment Pending Outstanding Balance", 14, y);
      doc.text(`Rs ${Number(totals.partialPendingAmount).toLocaleString()}`, 120, y);

      const paidRate = totals.totalInvoices ? ((totals.paidInvoices / totals.totalInvoices) * 100).toFixed(1) : 0;
      y += 8;
      doc.text("Invoice Fulfillment Rate", 14, y);
      doc.text(`${paidRate}%`, 120, y);

      y += 18;
    }

    if (type === "full") {
      // Insights Box
      doc.setFillColor(245, 247, 251);
      doc.rect(14, y, pageWidth - 28, 22, "F");

      doc.setTextColor(11, 31, 58);
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.text("General Summary Dashboard Insight:", 18, y + 8);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(80, 80, 80);

      const statusNote = totals.pendingInvoices > 0
        ? `There are currently ${totals.pendingInvoices} unpaid/partial invoices pending fulfillment.`
        : "All invoices are fully settled.";
      doc.text(`- Projections show proposals conversion is sitting at ${totals.totalProposals ? ((totals.approvedProposals / totals.totalProposals) * 100).toFixed(1) : 0}%. ${statusNote}`, 18, y + 15);
      y += 30;
    }

    // Detailed Proposals List Panel
    if (type === "full" || type === "proposals") {
      if (y + 35 > 275) {
        doc.addPage();
        y = 20;
      }
      doc.setTextColor(11, 31, 58);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Detailed Proposals List", 14, y);
      y += 3;
      doc.setDrawColor(200, 200, 200);
      doc.line(14, y, pageWidth - 14, y);
      y += 6;

      const printPropHeader = () => {
        doc.setFillColor(240, 244, 248);
        doc.rect(14, y - 5, pageWidth - 28, 8, "F");
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(11, 31, 58);
        doc.text("Proposal ID", 17, y + 0.5);
        doc.text("Title", 48, y + 0.5);
        doc.text("Client Name", 108, y + 0.5);
        doc.text("Price", 158, y + 0.5);
        doc.text("Status", 185, y + 0.5);
        y += 7;
      };

      printPropHeader();

      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);

      const proposalsList = data.proposalsList || [];
      if (proposalsList.length === 0) {
        doc.text("No proposals recorded.", 17, y + 2);
        y += 8;
      } else {
        proposalsList.forEach((p) => {
          if (y + 10 > 275) {
            doc.addPage();
            y = 25;
            printPropHeader();
            doc.setFont("helvetica", "normal");
            doc.setTextColor(50, 50, 50);
          }

          const propId = p.purposalId || "N/A";
          const title = p.title || "Untitled";
          const clientName = p.clientId?.leadName || "N/A";
          const price = p.price || p.services?.reduce((sum, s) => sum + (Number(s.price) || 0), 0) || 0;
          const status = p.status || "Draft";

          doc.text(truncateText(propId, 12), 17, y + 2);
          doc.text(truncateText(title, 26), 48, y + 2);
          doc.text(truncateText(clientName, 22), 108, y + 2);
          doc.text(`Rs ${Number(price).toLocaleString()}`, 158, y + 2);
          doc.text(status, 185, y + 2);

          y += 7;
          doc.setDrawColor(240, 240, 240);
          doc.line(14, y - 3, pageWidth - 14, y - 3);
        });
      }
      y += 10;
    }

    // Detailed Invoices List Panel
    if (type === "full" || type === "invoices") {
      if (y + 35 > 275) {
        doc.addPage();
        y = 20;
      }
      doc.setTextColor(11, 31, 58);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("Detailed Invoices List", 14, y);
      y += 3;
      doc.setDrawColor(200, 200, 200);
      doc.line(14, y, pageWidth - 14, y);
      y += 6;

      const printInvHeader = () => {
        doc.setFillColor(240, 244, 248);
        doc.rect(14, y - 5, pageWidth - 28, 8, "F");
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(11, 31, 58);
        doc.text("Invoice No", 17, y + 0.5);
        doc.text("Client Name", 48, y + 0.5);
        doc.text("Total", 98, y + 0.5);
        doc.text("Paid", 125, y + 0.5);
        doc.text("Remaining", 152, y + 0.5);
        doc.text("Status", 182, y + 0.5);
        y += 7;
      };

      printInvHeader();

      doc.setFont("helvetica", "normal");
      doc.setTextColor(50, 50, 50);

      const invoicesList = data.invoicesList || [];
      if (invoicesList.length === 0) {
        doc.text("No invoices recorded.", 17, y + 2);
        y += 8;
      } else {
        invoicesList.forEach((inv) => {
          if (y + 10 > 275) {
            doc.addPage();
            y = 25;
            printInvHeader();
            doc.setFont("helvetica", "normal");
            doc.setTextColor(50, 50, 50);
          }

          const invNo = inv.invoiceNumber || "N/A";
          const clientName = inv.clientName || "N/A";
          const curr = inv.currency || "INR";
          const total = inv.totalAmount || 0;
          const paid = inv.paidAmount || 0;
          const remaining = typeof inv.remainingAmount === "number"
            ? inv.remainingAmount
            : Math.max(0, total - paid);
          const status = inv.status || "Pending";

          doc.text(truncateText(invNo, 12), 17, y + 2);
          doc.text(truncateText(clientName, 22), 48, y + 2);
          doc.text(`${curr} ${Number(total).toLocaleString()}`, 98, y + 2);
          doc.text(`${curr} ${Number(paid).toLocaleString()}`, 125, y + 2);
          doc.text(`${curr} ${Number(remaining).toLocaleString()}`, 152, y + 2);
          doc.text(status, 182, y + 2);

          y += 7;
          doc.setDrawColor(240, 240, 240);
          doc.line(14, y - 3, pageWidth - 14, y - 3);
        });
      }
    }

    // Dynamic Footer Page Numbers
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(150, 150, 150);
      doc.text("Confidential. Generated by Premier Webtech Admin System.", 14, 288);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 26, 288);
    }

    const docName = type === "full"
      ? "Business_Intelligence_Report.pdf"
      : type === "proposals"
        ? "Proposals_Performance_Report.pdf"
        : "Invoices_Performance_Report.pdf";
    doc.save(docName);
  };

  const downloadExcelReport = (type = "full") => {
    if (!data) return;

    const wb = XLSX.utils.book_new();

    // 1. Summary Sheet
    let summaryData = [];
    if (type === "full" || type === "proposals") {
      summaryData.push(
        { Category: "Proposals", Metric: "Total Proposals", Value: totals.totalProposals },
        { Category: "Proposals", Metric: "Approved Proposals", Value: totals.approvedProposals },
        { Category: "Proposals", Metric: "Pending Proposals", Value: totals.pendingProposals }
      );
    }
    if (type === "full" || type === "invoices") {
      summaryData.push(
        { Category: "Invoices", Metric: "Total Invoices", Value: totals.totalInvoices },
        { Category: "Invoices", Metric: "Paid Invoices", Value: totals.paidInvoices },
        { Category: "Invoices", Metric: "Pending Invoices", Value: totals.pendingInvoices },
        { Category: "Invoices", Metric: "Partial Payment Pending Amount", Value: totals.partialPendingAmount }
      );
    }
    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    const summarySheetName = type === "full" ? "Summary Overview" : type === "proposals" ? "Proposals Summary" : "Invoices Summary";
    XLSX.utils.book_append_sheet(wb, wsSummary, summarySheetName);

    // 2. Proposals Sheet
    if (type === "full" || type === "proposals") {
      const detailedProposalsRows = (data.proposalsList || []).map((p) => ({
        "Proposal ID": p.purposalId || "N/A",
        "Title": p.title || "Untitled",
        "Client Name": p.clientId?.leadName || "N/A",
        "Total Price (Rs)": p.price || p.services?.reduce((sum, s) => sum + (Number(s.price) || 0), 0) || 0,
        "Status": p.status || "Draft",
        "Created Date": p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "N/A",
      }));
      const wsProposals = XLSX.utils.json_to_sheet(detailedProposalsRows);
      XLSX.utils.book_append_sheet(wb, wsProposals, "Detailed Proposals");
    }

    // 3. Invoices Sheet
    if (type === "full" || type === "invoices") {
      const detailedInvoicesRows = (data.invoicesList || []).map((inv) => {
        const rem = typeof inv.remainingAmount === "number"
          ? inv.remainingAmount
          : Math.max(0, (inv.totalAmount || 0) - (inv.paidAmount || 0));
        return {
          "Invoice Number": inv.invoiceNumber || "N/A",
          "Client Name": inv.clientName || "N/A",
          "Client Email": inv.clientEmail || "N/A",
          "Subtotal Amount": inv.subTotalAmount || 0,
          "Tax Name": inv.taxName || "N/A",
          "Tax Amount": inv.taxAmount || 0,
          "Total Amount": inv.totalAmount || 0,
          "Paid Amount": inv.paidAmount || 0,
          "Remaining Amount": rem,
          "Currency": inv.currency || "INR",
          "Status": inv.status || "Pending",
          "Invoice Date": inv.date ? new Date(inv.date).toLocaleDateString() : "N/A",
          "Due Date": inv.dueDate ? new Date(inv.dueDate).toLocaleDateString() : "N/A",
        };
      });
      const wsInvoices = XLSX.utils.json_to_sheet(detailedInvoicesRows);
      XLSX.utils.book_append_sheet(wb, wsInvoices, "Detailed Invoices");
    }

    const fileName = type === "full"
      ? "Business_Intelligence_Summary.xlsx"
      : type === "proposals"
        ? "Proposals_Performance_Summary.xlsx"
        : "Invoices_Performance_Summary.xlsx";
    XLSX.writeFile(wb, fileName);
  };

  const barData = useMemo(
    () => ({
      labels: ["Proposals", "Invoices"],
      datasets: [
        {
          label: "Pending",
          data: [totals.pendingProposals, totals.pendingInvoices],
          backgroundColor: "rgba(168, 85, 247, 0.82)",
          borderRadius: 8,
        },
        {
          label: "Approved/Paid",
          data: [totals.approvedProposals, totals.paidInvoices],
          backgroundColor: "rgba(20, 184, 166, 0.82)",
          borderRadius: 8,
        },
      ],
    }),
    [totals]
  );

  const pieData = useMemo(
    () => ({
      labels: ["Proposals", "Invoices"],
      datasets: [
        {
          label: "Total",
          data: [totals.totalProposals, totals.totalInvoices],
          backgroundColor: ["rgba(129, 140, 248, 0.84)", "rgba(45, 212, 191, 0.82)"],
          borderWidth: 0,
        },
      ],
    }),
    [totals]
  );

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: "#000000",
          font: { size: 12, weight: "600" },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: "#000000" },
        grid: { color: "rgba(148, 163, 184, 0.16)" },
      },
      y: {
        ticks: { color: "#000000" },
        grid: { color: "rgba(148, 163, 184, 0.14)" },
      },
    },
  };

  if (loading) {
    return <div className="reports-state">Loading reports data...</div>;
  }

  if (error) {
    return <div className="reports-state error">Error: {error}</div>;
  }

  return (
    <div className="reports-page">
      <div className="reports-hero">
        <div className="reports-hero-left">
          <p className="reports-overline">Business Intelligence</p>
          <h2>Reports Dashboard</h2>
          <p className="reports-subline">Track proposal and invoice performance in one place</p>
        </div>
        <div className="reports-hero-actions" style={{ position: "relative" }}>
          <button className="reports-download-btn" onClick={(e) => { e.stopPropagation(); setShowDropdown(!showDropdown); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: "6px" }}>
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download Report
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: "6px" }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showDropdown && (
            <div className="reports-dropdown-menu" onClick={(e) => e.stopPropagation()}>
              <div className="reports-dropdown-header">Full Report</div>
              <button className="reports-dropdown-item" onClick={() => { downloadPDFReport("full"); setShowDropdown(false); }}>
                PDF Format
              </button>
              <button className="reports-dropdown-item" onClick={() => { downloadExcelReport("full"); setShowDropdown(false); }}>
                Excel Format
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="reports-kpi-grid">
        <article className="reports-kpi-card" onClick={() => navigate("/admin/PurposalList")}>
          <p>Total Proposals</p>
          <h3>{totals.totalProposals}</h3>
        </article>
        <article className="reports-kpi-card" onClick={() => navigate("/admin/PurposalList", { state: { search: "Accepted" } })}>
          <p>Approved Proposals</p>
          <h3>{totals.approvedProposals}</h3>
        </article>
        <article className="reports-kpi-card" onClick={() => navigate("/admin/InvoicesList")}>
          <p>Total Invoices</p>
          <h3>{totals.totalInvoices}</h3>
        </article>
        <article className="reports-kpi-card" onClick={() => navigate("/admin/InvoicesList", { state: { search: "Paid" } })}>
          <p>Paid Invoices</p>
          <h3>{totals.paidInvoices}</h3>
        </article>
        <article className="reports-kpi-card highlight" onClick={() => navigate("/admin/InvoicesList", { state: { search: "Partial" } })}>
          <p>Partial Payment Pending</p>
          <h3>Rs {Number(totals.partialPendingAmount).toLocaleString()}</h3>
        </article>
      </div>

      <div className="reports-chart-grid">
        <article className="reports-chart-card">
          <h5>Proposal vs Invoice Status</h5>
          <div className="reports-chart-box">
            <Bar data={barData} options={chartOptions} />
          </div>
        </article>

        <article className="reports-chart-card">
          <h5>Total Distribution</h5>
          <div className="reports-chart-box">
            <Pie data={pieData} options={{ maintainAspectRatio: false, plugins: chartOptions.plugins }} />
          </div>
        </article>
      </div>
    </div>
  );
}

export default ReportsDashboard;

